<template>
  <v-container class="chat-wrapper" fluid>
    <!-- Model Select -->
    <!-- <v-row>
      <v-card-title class="model-select">
        <v-select 
          v-model="selectedModel" 
          variant="solo" 
          class="custom-field" 
          :items="models" 
          item-value="name"
          item-title="company" 
          placeholder="Select a Model" 
          density="compact"
          :menu-props="{ disabled: true }"
          :readonly="true"
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props" class="d-flex align-center">
              <v-avatar left size="24" class="mr-2">
                <img :src="item.raw.icon" alt="icon" />
              </v-avatar>
              <span>{{ item.raw.name }}</span>
            </v-list-item>
          </template>

<template #selection="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="24" class="mr-2">
                <img :src="item.raw.icon" alt="icon" />
              </v-avatar>
              <span>{{ item.raw.name }}</span>
            </div>
          </template>
</v-select>

</v-card-title>
<v-divider class="divider"></v-divider>
</v-row> -->

    <!-- Chat History -->
    <v-row v-if="!getFirstMsg" class="chat-area">
      <v-col cols="12">
        <!-- Responsive height for chat card -->
        <v-card :style="chatCardStyle" class="d-flex flex-column">
          <!-- Chat History Scrollable -->
          <div class="chat-history flex-grow-1" ref="chatHistory" 
               @click="handleChatClick"
               @mousedown="handleMouseDown"
               @mouseup="handleMouseUp"
               @scroll="handleScroll">
            <v-list dense>
              <v-list-item v-for="(msg, index) in history" :key="index">
                <v-list-item-content>
                  <div v-if="msg.role === 'user'" class="profile-Logo">
                    {{ userInitials }}
                  </div>
                  <div v-if="msg.role === 'ai'" class="ai-Logo">
                    <img :src="aiLogo" alt="AI Logo" />
                  </div>
                  <div v-if="msg.role === 'user'" class="right chat-bubble">
                    <div v-html="renderMarkdown(msg.context)"></div>
                  </div>
                  
                  <div v-else class="left chat-bubble">
                    <!-- Text content -->
                    <div v-if="!msg.type || msg.type === 'text'" v-html="renderMarkdown(msg.context)"></div>
                    <!-- HTML content (iframe for plots) -->
                    <div v-else-if="msg.type === 'html'" class="html-content">
                      <iframe 
                        :srcdoc="msg.context" 
                        class="plot-iframe"
                        frameborder="0"
                        scrolling="no"
                        sandbox="allow-scripts allow-same-origin"
                        @load="adjustIframeHeight">
                      </iframe>
                    </div>
                    <div class="feedback-icons" style="display: flex; gap: 8px; margin-top: 4px;">
                      <img @click="handleFeedback(index, true)" 
                           :src="likeImg" 
                           alt="Thumbs Up"
                           :class="{ 'active-feedback': likeIndex === index }"
                           style="width: 20px; height: 20px; cursor: pointer;" />
                      <img @click="handleFeedback(index, false)" 
                           :src="dislikeImg" 
                           alt="Thumbs Down"
                           :class="{ 'active-feedback': dislikeIndex === index }"
                           style="width: 20px; height: 20px; cursor: pointer;" />
                    </div>
                    
                    <!-- Feedback Form - appears below the message -->
                    <div v-if="dislikeIndex === index" class="feedback-form-below">
                      <div class="feedback-input-container">
                        <textarea v-model="feedbackText"
                          class="feedbackForm w-full bg-gray-100 text-sm text-gray-800 placeholder-gray-500 p-3 rounded-md focus:outline-none resize-none"
                          placeholder="What went wrong" rows="3"></textarea>
                        <button @click="closeFeedback" class="feedback-close-btn">✕</button>
                      </div>
                      <button @click="submitFeedback(index)"
                        class="feedbackButton mt-2 text-sm px-4 py-1.5 rounded-md hover:bg-[#002c3a] transition">
                        Submit Feedback
                      </button>
                    </div>
                  </div>

                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- Input Area -->
          <div class="input-wrapper">
            <div class="inner-chat-input-container" 
                 @dragover="handleDragOver" 
                 @drop="handleDrop"
                 :class="{ 'drag-over': isDragOver, 'disabled': loading }"
                 @dragenter="isDragOver = true"
                 @dragleave="isDragOver = false">
              <!-- File Upload Icon (Paperclip) -->
              <div class="inner-file-upload-icon" @click.stop="triggerFileUpload" :class="{ 'disabled': loading }">
                <img :src="require('@/assets/Images/FDPal/paperclip-2.svg')" alt="Upload File" />
              </div>
              
              <v-text-field v-model="message" placeholder="Ask a question about fluids characterization data, or drag and drop your PVT pdf file..." hide-details variant="solo" flat rounded
                class="custom-chat-input" @keyup.enter="sendMessage">
                <template #append-inner>
                  <v-progress-circular v-if="loading" indeterminate color="primary"></v-progress-circular>
                  <img v-else :src="sendImg" alt="Send" class="send-icon" @click="sendMessage" />
                </template>
              </v-text-field>
            </div>
            
            <!-- Hidden file input for inner chat -->
            <input 
              type="file" 
              ref="innerFileInput" 
              @change="handleFileSelect" 
              accept=".pdf" 
              style="display: none;"
            />
            
            <!-- Helper text when PDF is uploaded -->
            <div v-if="fileName && falsePDF && !pdfContextUsed" class="pdf-context-helper">
              <small class="text-muted">
                <v-icon small color="info">info</v-icon>
                PDF file uploaded successfully. You can now ask questions about this report.
              </small>
            </div>
          </div>
          <!-- Suggestion Buttons - Always visible -->
          <div class="button-row">
            <!-- Buttons when no PDF is uploaded -->
            <template v-if="!isPDFUploaded">
              <v-btn outlined small 
                     :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                     :disabled="loading"
                     @click="handlePromptClick('summarize', 'Summarize data from database for well: (add your well name here)')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize data
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected from database for well: (add your well name). Provide me the details of the samples from this well.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble Point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew Point
                </v-btn>
            </template>
            
            <!-- Buttons when PDF is uploaded -->
            <template v-else>
              <v-btn outlined small 
                     :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                     :disabled="loading"
                     @click="handlePromptClick('summarize', 'Summarize this PVT report.')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize Report
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected for this study? Provide me the details of the samples in this study.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the fluid in this report?')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew point
                </v-btn>
            </template>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Initial Empty View -->
    <v-row v-else justify="center" align="center" class="fill-height">
      <v-col cols="12" md="auto">
        
        <v-card class="chat-card" elevation="3">
          <div>
            <!-- Title -->
            <h1 class="chat-title">Fluids Intelligence Starts Here</h1>
            
            <!-- Initial Input -->
            <div class="chat-box" 
                 @dragover="handleDragOver" 
                 @drop="handleDrop"
                 @click="triggerFileUpload" 
                 :class="{ 'drag-over': isDragOver, 'disabled': loading }"
                 @dragenter="isDragOver = true"
                 @dragleave="isDragOver = false">
              <!-- File Upload Icon (Paperclip) -->
              <div class="file-upload-icon" @click.stop="triggerFileUpload" :class="{ 'disabled': loading }">
                <img :src="require('@/assets/Images/FDPal/paperclip-2.svg')" alt="Upload File" />
              </div>
              
              <v-textarea v-model="message" placeholder="Ask a question about fluids characterization data, or drag and drop your PVT pdf file..." rounded flat no-resize
                class="input-box" @click.stop></v-textarea>
              
              <!-- Send Icon (Paper Airplane) -->
              <div class="send-icon-wrapper" @click.stop="sendMessage">
                <v-progress-circular v-if="loading" indeterminate color="primary" size="20"></v-progress-circular>
                <img v-else :src="sendImg" alt="Send" />
              </div>
            </div>

            <!-- Hidden file input -->
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileSelect" 
              accept=".pdf" 
              style="display: none;"
            />

            <!-- Helper text when PDF is uploaded -->
            <div v-if="fileName && falsePDF && !pdfContextUsed" class="pdf-context-helper">
              <small class="text-muted">
                <v-icon small color="info">info</v-icon>
                PDF file uploaded successfully. You can now ask questions about this report.
              </small>
            </div>

            <!-- Button Row -->
            <div class="button-row">
              <!-- Buttons when no PDF is uploaded -->
              <template v-if="!isPDFUploaded">
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                       :disabled="loading"
                       @click="handlePromptClick('summarize', 'Summarize data from database for well: (add your well name here)')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize data
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected from database for well: (add your well name). Provide me the details of the samples from this well.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble Point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew Point
                </v-btn>
              </template>
              
              <!-- Buttons when PDF is uploaded -->
              <template v-else>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                       :disabled="loading"
                       @click="handlePromptClick('summarize', 'Summarize this PVT report.')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize Report
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected for this study? Provide me the details of the samples in this study.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the fluid in this report?')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew point
                </v-btn>
              </template>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarTimeout" :top="snackbarTop">
    {{ snackbarText }}
    <template v-slot:action="{ attrs }">
      <v-btn text v-bind="attrs" @click="snackbar = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import axios from 'axios';
import MarkdownIt from 'markdown-it';
import { mapGetters } from 'vuex';
const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true
});

export default {
  props: {
    uploadedFile: {
      type: File,
      default: null,
    },
  },
  data() {
    return {
      selectedModel: 'gpt-4o',
      models: [
        {
          name: 'gpt-4o',
          company: 'OpenAI',
          icon: require('../../assets/Images/FDPal/models/gpt4o.svg'),
        },
        {
          name: 'Llama-3.3-70B-Instruct',
          company: 'Meta',
          icon: require('../../assets/Images/FDPal/models/llama3.svg'),
        },
        {
          name: 'Phi-4',
          company: 'Microsoft',
          icon: require('../../assets/Images/FDPal/models/phi4.svg'),
        },
        {
          name: 'Meta-Llama-3.1-405B-Instruct',
          company: 'Meta',
          icon: require('../../assets/Images/FDPal/models/llama3.svg'),
        },
        {
          name: 'DeepSeek-R1',
          company: 'DeepSeek',
          icon: require('../../assets/Images/FDPal/models/deepseek.svg'),
        },
      ],
      message: '',
      sendImg: require("../../assets/Images/FDPal/send-2.svg"),
      likeImg: require("../../assets/Images/FDPal/like.svg"),
      dislikeImg: require("../../assets/Images/FDPal/dislike.svg"),
      aiLogo: require("../../assets/Images/FDPal/aiLogo.png"),
      chatContext: '',
      history: [],
      loading: false,
      snackbar: false,
      snackbarText: 'Ready to help with your fluids analysis questions!',
      snackbarColor: 'error',
      snackbarTimeout: 6000,
      snackbarTop: true,
      windowHeight: window.innerHeight,
      dislikeIndex: null,
      feedbackText: '',
      getFirstMsg: true,
      falsePDF: false,
      isDragOver: false,
      localUploadedFile: null, // Local file reference
      fileName: null, // Store the uploaded file name
      likeIndex: null, // Track the index of the like button
      activePromptButton: null, // Track the active prompt button
      pdfContextUsed: false, // Track if PDF context has been used for first message
      isUploading: false, // Flag to prevent duplicate uploads
      originalScrollTop: 0, // Store original scroll position
      justSentMessage: false, // Flag to track if message was just sent
      scrollPreventionTimeout: null, // Timeout for scroll prevention
      preventScroll: false, // Flag to prevent scrolling
      lastScrollPosition: 0, // Store last scroll position
    };
  },
  computed: {
    ...mapGetters("tenantId", ["getUser"]),
    userInitials() {
      const user = this.getUser;
      if (user && user.firstName && user.lastName) {
        return (
          (user.firstName[0] || '').toUpperCase() +
          (user.lastName[0] || '').toUpperCase()
        );
      }
      return '';
    },
    chatCardStyle() {
      // Responsive height: 
      // - On large screens, use a max height (e.g. 65vh or 700px)
      // - On small screens, use a higher vh
      // This prevents the input from being pushed off the screen on large monitors
      if (this.windowHeight > 900) {
        // Large screens (e.g. 17" 1080p+)
        return {
          height: '85vh',
          maxHeight: '800px',
          minHeight: '400px',
        };
      } else if (this.windowHeight > 700) {
        // Medium screens
        return {
          height: '90vh',
          maxHeight: '90vh',
          minHeight: '350px',
        };
      } else {
        // Small screens
        return {
          height: '75vh',
          maxHeight: '95vh',
          minHeight: '250px',
        };
      }
    },
    isPDFUploaded() {
      return this.fileName !== null && this.falsePDF;
    }
  },
  methods: {
    async handleFeedback(index, isPositive) {
      if (isPositive) {
        // Handle like button
        if (this.likeIndex === index) {
          this.likeIndex = null; // Deselect if already selected
        } else {
          this.likeIndex = index;
          this.dislikeIndex = null; // Deselect dislike if it was selected
        }
        return;
      }

      // Handle dislike button
      if (this.dislikeIndex === index) {
        this.dislikeIndex = null;
        this.feedbackText = '';
        return;
      } else {
        this.dislikeIndex = index;
        this.likeIndex = null; // Deselect like if it was selected
        this.feedbackText = '';
      }

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_LLM_URL}feedback`,
          {
            message_index: index,
            is_positive: isPositive,
            feedback_text: isPositive ? null : this.feedbackText
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem("authToken")}`
            }
          }
        );

        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }

        if (isPositive) {
          this.snackbarText = 'Thank you for your positive feedback!';
          this.snackbarColor = 'success';
        } else {
          this.snackbarText = 'Thank you for your feedback!';
          this.snackbarColor = 'success';
        }

        this.snackbar = true;
      } catch (error) {
        console.error("Feedback error:", error);
      } finally {
        if (isPositive) {
          this.dislikeIndex = null;
          this.feedbackText = '';
        }
      }
    },

    async submitFeedback(index) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_LLM_URL}feedback`,
          {
            message_index: index,
            is_positive: false,
            feedback_text: this.feedbackText
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem("authToken")}`
            }
          }
        );

        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }

        // Show success message
        this.snackbarText = 'Thank you for your feedback!';
        this.snackbarColor = 'success';
        this.snackbar = true;

        // Reset feedback form
        this.dislikeIndex = null;
        this.feedbackText = '';

        // Refresh chat history
        await this.getHistory();

      } catch (error) {
        console.error("Feedback error:", error);
        this.snackbarText = 'Error submitting feedback';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },
    toggleDislike(index) {
      if (this.dislikeIndex === index) {
        this.dislikeIndex = null;
      } else {
        this.dislikeIndex = index;
        this.feedbackText = '';
      }
    },
    renderMarkdown(text) {
      return md.render(text || '');
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          // Only scroll to bottom if user is already near the bottom
          const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
          if (isNearBottom) {
            container.scrollTop = container.scrollHeight;
          }
        }
      });
    },
    forceScrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    async uploadPdf() {
      if (this.isUploading) {
        return; // Prevent duplicate uploads
      }
      this.isUploading = true;
      this.loading = true; // Start loading for the upload process
      const formData = new FormData();
      this.falsePDF = false;
      formData.append('file_upload', this.localUploadedFile);

      // Show upload in progress message
      this.snackbarText = 'Upload in progress...';
      this.snackbarColor = 'info';
      this.snackbar = true;

      try {
        const response = await axios.post(`${process.env.VUE_APP_LLM_URL}upload-pdf`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`
          }
        });

        if (response.data.status === 401) {
          this.snackbarText = 'Session expired. Please login again.';
          this.snackbarColor = 'error';
          this.snackbar = true;
          this.$router.push('/login');
          return;
        }

        this.fileName = response.data.response;
        this.falsePDF = true;
        this.pdfContextUsed = false; // Reset flag for new PDF
        
        // Fill the auto-prompt into the input field instead of automatically sending
        const pdfMessage = `Summarize this PVT report pdf: ${this.fileName}`;
        this.message = pdfMessage;
        
        // Show success message
        this.snackbarText = 'File uploaded successfully!';
        this.snackbarColor = 'success';
        this.snackbar = true;
        
        // Don't automatically send the message - let user review and send manually
        this.loading = false; // Reset loading since we're not auto-sending

      } catch (error) {
        this.falsePDF = false;
        console.error("Upload error:", error);
        
        // Show appropriate error message
        if (error.response) {
          this.snackbarText = error.response.data.message || 'Error uploading file';
        } else if (error.request) {
          this.snackbarText = 'No response from server. Please try again.';
        } else {
          this.snackbarText = 'Error uploading file. Please try again.';
        }
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false; // Reset loading on error
      } finally {
        this.isUploading = false; // Reset upload flag
        // Note: loading state is managed by sendMessage method
      }
    },
    async sendMessage() {
      if (this.loading || !this.message.trim()) return;
      if (this.message.trim()) {
        this.getFirstMsg = false;
        // Show loader for messages
        this.loading = true; // Start loading animation
        const userMsg = this.message;
        this.history.push({ role: 'user', context: userMsg });
        this.message = '';
        // Force scroll to bottom when user sends a message
        this.forceScrollToBottom();

        // Set flag to prevent unwanted scrolling after message
        this.justSentMessage = true;
        
        // Clear any existing timeout
        if (this.scrollPreventionTimeout) {
          clearTimeout(this.scrollPreventionTimeout);
        }
        
        // Set timeout to allow scrolling again after 3 seconds
        this.scrollPreventionTimeout = setTimeout(() => {
          this.justSentMessage = false;
        }, 3000);

        // Send question - with or without file
        if (this.localUploadedFile && this.fileName && !this.pdfContextUsed) {
          await this.askQuestion(userMsg);
          // Mark PDF context as used after first message
          this.pdfContextUsed = true;
        } else {
          await this.askQuestion(userMsg);
        }
      }
    },
    async askQuestion(userMsg) {
      // Remove file validation - allow questions without files
      if (this.localUploadedFile && !this.falsePDF) {
        this.loading = false;
        return;
      }
      
      // Prepare the question data with filename as parameter if available
      let questionText = userMsg;
      let questionData = {
        message: questionText
      };
      
      // If there's an uploaded PDF and it's the first time, add filename as parameter
      if (this.fileName && this.falsePDF && !this.pdfContextUsed) {
        questionData.source_pdf = this.fileName;
        // Use the user's message as is, since filename is now a parameter
        questionData.message = userMsg;
      }
      
      try {
        // Keep loading state active during API call
        // this.loading is already true from sendMessage method
        const response = await axios.post(`${process.env.VUE_APP_LLM_URL}api/chat`, questionData, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`
          }
        });
        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }
        // Handle new response format with content and type
        const responseData = response.data.response;
        if (typeof responseData === 'object' && responseData.content && responseData.type) {
          this.chatContext = responseData.content;
          this.history.push({ 
            role: 'ai', 
            context: responseData.content, 
            type: responseData.type 
          });
        } else {
          // Fallback for old format
          this.chatContext = responseData;
          this.history.push({ role: 'ai', context: responseData });
        }
        this.scrollToBottom();
      } catch (error) {
        console.error("Answer Question error:", error);
        // Show error message to user
        this.snackbarText = 'Error getting response. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.loading = false; // Stop loading animation
      }
    },
    async getHistory() {
      this.loading = true; // Start loading animation
      try {
        const response = await axios.post(`${process.env.VUE_APP_LLM_URL}get-history`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`
          }
        });
        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }
        const lastUserMessage = response.data.response
          .filter(item => item.role === "user")
          .slice(-1)[0];
        this.history = response.data.response.map(item => {
          let content = item.content;
          let type = item.type || 'text'; // Default to text type
          
          if (item.role !== "ai") {
            const regex = /Question:\s*([^]*)/;
            const match = content.match(regex);
            content = match ? match[1] : content;

            if (lastUserMessage) {
              const pdfRegex = /pdf\s*:\s*(.+?\.pdf)/i;
              const pdfMatch = content.match(pdfRegex);
              if (pdfMatch) {
                this.fileName = pdfMatch[1];
              }
            }
          }
          return {
            role: item.role,
            context: content,
            type: type
          };
        });
        // Don't auto-scroll when loading history - let user stay where they are
        // this.scrollToBottom();
      } catch (error) {
        console.error("Get History error:", error);
      } finally {
        this.loading = false; // Stop loading animation
      }
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    },
    handleDragOver(event) {
      if (this.loading) return; // Prevent drag and drop during loading
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
    },
    handleDrop(event) {
      if (this.loading) return; // Prevent file drop during loading
      event.preventDefault();
      event.stopPropagation();
      this.isDragOver = false; // Reset drag-over state
      const file = event.dataTransfer.files[0];
      if (file && file.type === 'application/pdf' && !this.isUploading) {
        this.localUploadedFile = file;
        this.$emit('file-uploaded', file); // Emit to parent
        this.uploadPdf(); // Upload the PDF
      } else if (this.isUploading) {
        this.snackbarText = 'File upload already in progress. Please wait.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      } else {
        this.snackbarText = 'Please drop a PDF file.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      }
    },
    handleFileSelect(event) {
      if (this.loading) return; // Prevent file selection during loading
      const file = event.target.files[0];
      if (file && file.type === 'application/pdf' && !this.isUploading) {
        this.localUploadedFile = file;
        this.$emit('file-uploaded', file); // Emit to parent
        this.uploadPdf(); // Upload the PDF
      } else if (this.isUploading) {
        this.snackbarText = 'File upload already in progress. Please wait.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      } else {
        this.snackbarText = 'Please select a PDF file.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      }
      event.target.value = ''; // Clear the input
    },
    triggerFileUpload() {
      if (this.loading) return; // Prevent file upload during loading
      // Try to click the inner file input first, then fall back to the initial one
      if (this.$refs.innerFileInput) {
        this.$refs.innerFileInput.click();
      } else if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    closeFeedback() {
      this.dislikeIndex = null;
      this.feedbackText = '';
    },
    handlePromptClick(type, text) {
      this.activePromptButton = type;
      
      // If there's a PDF uploaded, append the filename to the prompt text
      if (this.fileName && this.falsePDF) {
        this.message = `${text} pdf: ${this.fileName}`;
      } else {
        // No PDF, just use the prompt text
        this.message = text;
      }
    },
    clearPDFState() {
      // Clear all PDF-related state
      this.localUploadedFile = null;
      this.fileName = null;
      this.falsePDF = false;
      this.pdfContextUsed = false;
      this.message = '';
      this.activePromptButton = null;
    },
    handleChatClick(event) {
      
      // Store current scroll position BEFORE any changes
      const container = this.$refs.chatHistory;
      if (container) {
        this.lastScrollPosition = container.scrollTop;
      }
      
      // Set flag to prevent any scrolling
      this.preventScroll = true;
      
      // Always prevent scrolling on click to chat-history
      event.preventDefault();
      event.stopPropagation();
      
      // Restore scroll position immediately
      if (container) {
        this.$nextTick(() => {
          container.scrollTop = this.lastScrollPosition;
        });
      }
      
      // Reset prevent scroll flag after a short delay
      setTimeout(() => {
        this.preventScroll = false;
      }, 100);
    },
    handleMouseDown(event) {
      // Store scroll position immediately on mousedown
      const container = this.$refs.chatHistory;
      if (container) {
        this.lastScrollPosition = container.scrollTop;
        this.preventScroll = true;
        
        // Enable aggressive scroll prevention
        if (this.preventScrollSetter) {
          this.preventScrollSetter(true);
        }
      }
      
      // Allow text selection by not preventing mousedown
      // Only prevent if it's not on text content
      const target = event.target;
      const isTextContent = target.closest('.chat-bubble') || 
                           target.closest('.v-list-item-content') ||
                           target.tagName === 'DIV' && target.textContent.trim();
      
      if (!isTextContent) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    handleMouseUp(event) {
      
      // Restore scroll position immediately on mouseup
      const container = this.$refs.chatHistory;
      if (container && this.preventScroll) {
        container.scrollTop = this.lastScrollPosition;
        
        // Disable aggressive scroll prevention
        if (this.preventScrollSetter) {
          this.preventScrollSetter(false);
        }
      }
      
      // Allow text selection by not preventing mouseup
      // Only prevent if it's not on text content
      const target = event.target;
      const isTextContent = target.closest('.chat-bubble') || 
                           target.closest('.v-list-item-content') ||
                           target.tagName === 'DIV' && target.textContent.trim();
      
      if (!isTextContent) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    handleScroll(event) {
      console.log('Scroll event triggered!', {
        scrollTop: event.target.scrollTop,
        timestamp: new Date().toISOString()
      });
      
      // If this scroll was caused by a click, prevent it
      if (this.preventScroll) {
        event.target.scrollTop = this.lastScrollPosition || 0;
        event.preventDefault();
        event.stopPropagation();
      }
    },
    adjustIframeHeight(event) {
      // Auto-adjust iframe height based on content
      const iframe = event.target;
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        if (iframeDoc) {
          const height = iframeDoc.body.scrollHeight;
          if (height > 0) {
            iframe.style.height = Math.min(height + 20, 600) + 'px'; // Max height of 600px
          }
        }
      } catch (error) {
        // If cross-origin restrictions prevent access, set a default height
        iframe.style.height = '400px';
      }
    },
  },
  watch: {
    uploadedFile(newFile) {
      if (newFile && !this.isUploading) {
        this.localUploadedFile = newFile; // Update local file reference
        // Don't call uploadPdf here since it's already handled by handleFileSelect/handleDrop
        // Just update the local reference
      } else if (newFile === "" || newFile === null) {
        // PDF was removed, reset internal state
        this.clearPDFState();
      }
    },
    message(newMessage) {
      // No need to enforce PDF context restoration since filename is now passed as parameter
      // The user can freely edit the message
    }
  },
  mounted() {
    this.getHistory(); // Call getHistory on mount
    window.addEventListener('resize', this.handleResize);
    
    // Override scroll behavior to prevent unwanted scrolling
    this.$nextTick(() => {
      const container = this.$refs.chatHistory;
      if (container) {
        // Store original scroll position
        this.originalScrollTop = container.scrollTop;
        
        // Add a more aggressive scroll prevention
        const originalScrollTop = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollTop');
        let isPreventingScroll = false;
        
        // Override scrollTop setter
        Object.defineProperty(container, 'scrollTop', {
          get: function() {
            return originalScrollTop.get.call(this);
          },
          set: function(value) {
            if (isPreventingScroll) {
              console.log('Preventing scrollTop change to:', value);
              return;
            }
            originalScrollTop.set.call(this, value);
          }
        });
        
        // Set up scroll prevention flag
        this.preventScrollSetter = (prevent) => {
          isPreventingScroll = prevent;
        };
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    // Clear any pending timeouts
    if (this.selectionTimeout) {
      clearTimeout(this.selectionTimeout);
    }
    if (this.scrollPreventionTimeout) {
      clearTimeout(this.scrollPreventionTimeout);
    }
  }
};
</script>

<style scoped>
.chat-wrapper {
  padding-top: 16px;
  padding-left: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.chat-card {
  padding-left: 16px;
  margin-top: 170px;
}

.chat-title {
  font-size: 27.50px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  margin-bottom: 30px;
  margin-top: 20px;
}

.custom-chat-input {
  font-size: 13.50px;
  font-weight: 400;
  background-color: transparent !important;
}

.custom-chat-input :deep(.v-field) {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.custom-chat-input :deep(.v-field__outline) {
  display: none !important;
}

.custom-chat-input :deep(.v-field--focused .v-field__field) {
  border-bottom: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.chat-box {
  position: relative;
  height: 140px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  transition: all 0.3s ease;
}

.file-upload-icon {
  position: absolute;
  bottom: 16px;
  left: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.file-upload-icon:hover {
  background-color: #f8f9fa;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.file-upload-icon img {
  width: 20px;
  height: 20px;
  opacity: 0.6;
}

.send-icon-wrapper {
  position: absolute;
  bottom: 16px;
  right: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
}

.send-icon-wrapper:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.send-icon-wrapper img {
  width: 20px;
  height: 20px;
  opacity: 0.6;
}

.chat-box.drag-over {
  background-color: #e3f2fd;
  border: 2px dashed #2196f3 !important;
  transform: scale(1.02);
}

.input-box {
  width: 564px;
  height: 134px;
}

.input-box :deep(.v-field) {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
::v-deep(.input-box .v-field__outline) {
  display: none;
}

::v-deep(.input-box .v-field--focused .v-field__field) {
  border-bottom: none !important;
  box-shadow: none !important;
}
.input-box :deep(textarea) {
  height: 100% !important;
  padding: 16px !important;
  line-height: 1.5 !important;
  resize: none;
  overflow-y: auto;
  background-color: transparent !important;
  
}

.chat-area {
  height: 100%;
}

.chat-history {
  overflow-y: auto;
  flex-grow: 1;
  max-height: 100%;
  pointer-events: auto;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  scroll-behavior: auto;
  -webkit-overflow-scrolling: touch;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  /* Prevent unwanted scrolling on click */
  overscroll-behavior: contain;
  /* Prevent any programmatic scrolling except when explicitly called */
  scroll-snap-type: none;
  /* Completely disable click-to-scroll behavior */
  scroll-padding: 0;
  scroll-margin: 0;
}

/* Disable all click-based scrolling */
.chat-history:active,
.chat-history:focus {
  scroll-behavior: auto !important;
}

.chat-history:focus,
.chat-history:active,
.chat-history:focus-visible {
  outline: none;
  box-shadow: none;
}

.chat-bubble {
  margin-bottom: 12px;
  border-radius: 10px;
  max-width: 75%;
  word-wrap: break-word;
  word-break: break-word;
  display: inline-block;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  cursor: text;
  pointer-events: auto;
  box-sizing: border-box;
  overflow-wrap: break-word;
}

/* Enable text selection for all text content in chat bubbles */
.chat-bubble * {
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

.left {
  align-self: flex-start;
  text-align: left;
  padding: 20px;
  float: left;
}

.feedbackForm {
  width: 100%;
  text-align: left;
  padding: 20px;
  float: left;
  background-color: rgba(0, 0, 0, 0.05)
}

.feedbackButton {
  background-color: rgba(14, 61, 85, 1);
  color: white;
  border-radius: 8px;
  padding: 10px;
  float: left;
}

.feedback-close-btn {
  background-color: #f0f0f0;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 15.50px;
  color: #888;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  margin-top: 8px;
}

.feedback-close-btn:hover {
  background-color: #e0e0e0;
}

.right {
  background-color: #d6f1ff;
  align-self: flex-end;
  padding: 10px;
  float: right;
  margin-left: auto;
  outline: 1px solid rgba(167, 214, 238, 1);
}


.input-wrapper {
  padding: 12px 6px 12px 12px;
}

.inner-chat-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 8px 12px;
  transition: all 0.3s ease;
}

.inner-chat-input-container.drag-over {
  background-color: #e3f2fd;
  border: 2px dashed #2196f3;
  transform: scale(1.02);
}

.inner-file-upload-icon {
  cursor: pointer;
  margin-right: 12px;
  padding: 6px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.inner-file-upload-icon:hover {
  background-color: #f8f9fa;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.inner-file-upload-icon img {
  width: 18px;
  height: 18px;
  opacity: 0.6;
}

.inner-file-upload-icon.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.file-upload-icon.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.send-btn {
  position: absolute;
  bottom: 20px;
  right: 24px;
  height: 24px;
  cursor: pointer;
}

.button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 12px;
  margin-bottom: 25px;
}

.v-btn {
  background-color: #f5f5f5 !important;
  border: 1px solid #ddd !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13.50px;
  padding: 6px 12px !important;
  min-width: auto !important;
}

.v-btn img {
  width: 18px;
  height: 18px;
  margin-right: 6px;
  vertical-align: middle;
}

.model-select {
  padding: 16px 16px 0 30px;
  top: 0;
  left: 0;
  width: 180px;
  transform-origin: top left;
}

.send-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-right: 8px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.send-icon:hover {
  opacity: 1;
}

.divider {
  margin-top: 2px;
}

::v-deep(.v-list-item__content) {
  overflow: visible !important;
}

::v-deep(.v-field__input),
::v-deep(.dp__input),
::v-deep(.v-select .v-select__selection-text),
::v-deep(.v-text-field .v-field--no-label input),
::v-deep(.input-info .v-field__input::placeholder),
::v-deep(.v-field__input::placeholder) {
  font-size: 13.50px;
}

.profile-Logo {
  width: 32px;
  height: 32px;
  background-color: #e1dbee;
  color: #555;
  font-weight: bold;
  font-family: sans-serif;
  font-size: 13.50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 88.89px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  margin-left: 8px;
  float: right;
  margin-top: 5px;
}

.ai-Logo {
  width: 32px;
  height: 32px;
  background-color: #e1dbee;
  color: #555;
  font-weight: bold;
  font-family: sans-serif;
  font-size: 13.50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  margin-right: 8px;
  float: left;
  margin-top: 5px;
  overflow: hidden;
}

.ai-Logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.feedback-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.feedback-title {
  font-size: 13.50px;
  font-weight: 600;
  color: #333;
}

.feedback-input-container {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.feedback-form-below {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.active-feedback {
  background-color: #e3f2fd !important;
  border-radius: 4px !important;
  padding: 2px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.active-prompt {
  background-color: #e3f2fd !important;
  border-color: #2196f3 !important;
  color: #1976d2 !important;
  box-shadow: 0 2px 4px rgba(33, 150, 243, 0.2) !important;
}

.active-prompt img {
  filter: brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%) !important;
}

.pdf-context-helper {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.50px;
  color: #1976d2;
}

.inner-chat-input-container.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.chat-box.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.feedback-icons {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: auto;
}

/* Enable pointer events for all interactive elements in chat history */
.chat-history .v-list-item,
.chat-history .v-list-item-content,
.chat-history .profile-Logo,
.chat-history .ai-Logo,
.chat-history .feedback-form-below,
.chat-history .feedback-input-container,
.chat-history .feedback-close-btn,
.chat-history .feedbackButton {
  pointer-events: auto;
}

/* Enable text selection for text content in chat history */
.chat-history .v-list-item,
.chat-history .v-list-item-content,
.chat-history .profile-Logo,
.chat-history .ai-Logo,
.chat-history .chat-bubble,
.chat-history .chat-bubble * {
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

/* HTML content styling for plots */
.html-content {
  width: 100%;
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fafafa;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.plot-iframe {
  width: 830px;    /* Set fixed width to match backend */
  max-width: 100%; /* Ensure it doesn’t overflow on smaller screens */
  height: 400px;
  border: none;
  display: block;
  background-color: white;
  border-radius: 8px;
  transition: height 0.3s ease;
}

/* Responsive iframe sizing */
@media (max-width: 768px) {
  .plot-iframe {
    width: 100%; /* Allow full width on tablets */
    height: 300px;
  }
}

@media (max-width: 480px) {
  .plot-iframe {
    width: 100%; /* Allow full width on mobile */
    height: 250px;
  }
}

</style>
